import { getChannelSeriesColor, Neutral500, Success600 } from '@src/utils/colors';
import { BRAND_COLOR_COUNT, BRAND_COLOR_SKIPPED_INDEX } from './alexa-visibility-constants';
import { isSameBrand, normalizeBrand } from './alexa-visibility-utils';

// The chosen brand is always green; each competitor gets its own color from Tinuiti's 20-color
// chart palette (the one for graphs with many series), skipping the palette's green so only the
// chosen brand is green. Every tab hands colors out in the same order (see `useBrandColor`), so a
// brand keeps its color on every page.
export const CHOSEN_BRAND_COLOR = Success600;
export const UNKNOWN_BRAND_COLOR = Neutral500;

export const getCompetitorColor = (index: number): string => {
  const slot = index % BRAND_COLOR_COUNT;
  return getChannelSeriesColor(slot < BRAND_COLOR_SKIPPED_INDEX ? slot : slot + 1, false);
};

/**
 * A brand -> color lookup. `competitors` is the ordered list that colors are dealt out from
 * (first competitor gets the first color); the target brand is always green and a brand that
 * isn't listed, or no brand at all, gets neutral grey.
 */
export const buildBrandColorResolver = (
  competitors: string[],
  targetBrand: string
): ((brand: string | undefined) => string) => {
  const colorByBrand = new Map<string, string>();
  competitors
    .filter(competitor => !isSameBrand(competitor, targetBrand))
    .forEach(competitor => {
      const key = normalizeBrand(competitor);
      if (!colorByBrand.has(key)) {
        colorByBrand.set(key, getCompetitorColor(colorByBrand.size));
      }
    });

  return brand => {
    if (!brand) return UNKNOWN_BRAND_COLOR;
    if (isSameBrand(brand, targetBrand)) return CHOSEN_BRAND_COLOR;
    return colorByBrand.get(normalizeBrand(brand)) ?? UNKNOWN_BRAND_COLOR;
  };
};
